// ============================================================
// TACTICS (Squad Mode stage 5, ruled 2026-10-23) - the library item that
// holds a table's squad abilities and the Oaths that unlock them.
//
// User-defined, never the book's lists: an ABILITY is a named set of
// effects (the stance effects, so they apply through the same machinery)
// with a Cohesion cost, the action it takes, and whether it is SUSTAINED
// (kept up for one cost) or a one-shot; an OATH ticks the abilities it
// unlocks and may add a Cohesion bonus. ONE item holds both (the user's
// ruling: "One Codex item"; the word Codex is the Threat Codex's, so the
// item is called Tactics). A session plays a COPY of it, and a kill-team
// picks one of its Oaths.
//
// Everything read off storage goes through `readTactics`, which keeps what
// this build can use and drops the rest - an Oath's tick of an ability no
// longer there among it.
//
// Pure. Zero React dependency.
// ============================================================
import { readEffects, type StanceEffect } from '../character/effectItems';
import { int, MAX_TEXT, nonZero, optional, text, type Raw } from './read';

/** The two kinds of squad ability the book names: a label for the reader, never a rule. */
export const ABILITY_CAPTIONS = ['Attack Pattern', 'Defensive Stance'] as const;
export type AbilityCaption = (typeof ABILITY_CAPTIONS)[number];

export const ABILITY_ACTIONS = ['free', 'half', 'full'] as const;
export type AbilityAction = (typeof ABILITY_ACTIONS)[number];

export interface SquadAbility {
  id: string;
  name: string;
  caption?: AbilityCaption;
  /** Cohesion it costs to start (0-20). */
  cost: number;
  action: AbilityAction;
  /** Kept up for its one cost until stopped; else a one-shot, whose effects last to each joiner's next turn. */
  sustained: boolean;
  /** What it does to those in it: stance effects. */
  effects: StanceEffect[];
  text?: string;
}

export interface Oath {
  id: string;
  name: string;
  /** Added to the starting Cohesion (-10..10). */
  cohesionBonus?: number;
  /** The abilities it unlocks, by id. */
  abilityIds: string[];
  text?: string;
}

export interface Tactics {
  id: string;
  name: string;
  abilities: SquadAbility[];
  oaths: Oath[];
}

const oneOf = <T extends string>(list: readonly T[], value: unknown): T | null =>
  list.includes(value as T) ? (value as T) : null;

function readAbility(raw: unknown, fresh: (id: string) => boolean): SquadAbility | null {
  const r = (raw ?? {}) as Raw;
  const id = text(r.id, 80);
  const name = text(r.name);
  if (!id || !name || !fresh(id)) return null;
  return {
    id,
    name,
    ...optional('caption', oneOf(ABILITY_CAPTIONS, r.caption)),
    cost: int(r.cost, 0, 20) ?? 1,
    action: oneOf(ABILITY_ACTIONS, r.action) ?? 'half',
    sustained: r.sustained === true,
    effects: readEffects(r.effects),
    ...optional('text', text(r.text, MAX_TEXT)),
  };
}

/** The fields an Oath and its copy on a sheet share (`killTeam/block`): null without an id or a name. */
export function readOathHead(raw: unknown): Pick<Oath, 'id' | 'name' | 'cohesionBonus' | 'text'> | null {
  const r = (raw ?? {}) as Raw;
  const id = text(r.id, 80);
  const name = text(r.name);
  if (!id || !name) return null;
  // Clamped to -10..10 like a cost, never dropped: a stored 12 was meant as a big bonus.
  return {
    id,
    name,
    ...optional('cohesionBonus', nonZero(int(r.cohesionBonus, -10, 10))),
    ...optional('text', text(r.text, MAX_TEXT)),
  };
}

function readOath(raw: unknown, fresh: (id: string) => boolean, abilities: ReadonlySet<string>): Oath | null {
  const head = readOathHead(raw);
  if (!head || !fresh(head.id)) return null;
  const ids = (raw as Raw).abilityIds;
  const ticks = Array.isArray(ids) ? ids.filter((a): a is string => abilities.has(a as string)) : [];
  return { ...head, abilityIds: [...new Set(ticks)] };
}

/** A minter of ids not seen before: true the first time, false after. */
function idsOnce(): (id: string) => boolean {
  const seen = new Set<string>();
  return (x) => !seen.has(x) && !!seen.add(x);
}

/** Stored abilities, validated: what will not read is dropped, and an id seen before (`fresh`) too. */
export const readAbilities = (raw: unknown, fresh: (id: string) => boolean = idsOnce()): SquadAbility[] =>
  (Array.isArray(raw) ? raw : []).map((a) => readAbility(a, fresh)).filter((a): a is SquadAbility => a !== null);

/** Stored Tactics, validated: null for what is not Tactics at all (no id or name). Ids are unique across the item. */
export function readTactics(raw: unknown): Tactics | null {
  const r = (raw ?? null) as Raw | null;
  const id = text(r?.id, 80);
  const name = text(r?.name);
  if (!r || !id || !name) return null;
  const fresh = idsOnce();
  const abilities = readAbilities(r.abilities, fresh);
  const ids = new Set(abilities.map((a) => a.id));
  const oaths = (Array.isArray(r.oaths) ? r.oaths : [])
    .map((o) => readOath(o, fresh, ids))
    .filter((o): o is Oath => o !== null);
  return { id, name, abilities, oaths };
}

/** An Oath's abilities, in the Tactics' own order; none for an Oath that is not there. */
export function oathAbilities(tactics: Tactics, oathId: string | null | undefined): SquadAbility[] {
  const oath = tactics.oaths.find((o) => o.id === oathId);
  if (!oath) return [];
  const ticked = new Set(oath.abilityIds);
  return tactics.abilities.filter((a) => ticked.has(a.id));
}

/** A new Tactics item: named, with nothing in it. */
export const blankTactics = (name: string, id: string): Tactics => ({ id, name, abilities: [], oaths: [] });
